// import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import React, { Fragment, memo, useEffect, useRef, useState } from 'react'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { CheckDataList } from 'src/components/global/ListingPage/CheckDataList'
import { DottedDataList } from 'src/components/global/ListingPage/DottedDataList'
import { IconLink } from 'src/components/global/ListingPage/IconLink'
import { SpanDataIcon } from 'src/components/global/ListingPage/SpanDataIcon'
import { SpanDataList } from 'src/components/global/ListingPage/SpanDataList'
import { LongDescItem } from 'src/components/global/Overview/LongDescItem'
import { ShortDescItem } from 'src/components/global/Overview/ShortDescItem'
import { FUNDING_DETAILS } from 'src/constants/constants'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { DedicatedResourcesType, SectionItem } from 'src/types'
import { checkNotEmpty } from 'src/utils/checkNotEmpty'

import CartHeader from '../Cart/CartHeader'
import CartHeaderTitle from '../Cart/CartHeaderTitle'
import CartSubContent from '../Cart/CartSubContent'
import CartSubTitle from '../Cart/CartSubTitle'
import { IconLinkLinkedin } from '../ListingPage/IconLinkLinkedin'
import { IconLinkTwitter } from '../ListingPage/IconLinkTwitter'
import { LikeHiddenComponent } from '../ListingPage/LikeHiddenComponent'
import { LockOpenComponent } from '../ListingPage/LockOpenComponent'
import { ThemeOnly } from '../ThemeOnly'

interface SectionProps {
  title: string
  shortDescription?: string
  description: string
  url: string
  items: SectionItem[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderItem?: (item: any) => React.ReactNode
  content?: string | TrustedHTML
  isFromCompany?: boolean
  unlockPaywalledContent?: boolean
}

export const Section: React.FC<SectionProps> = memo(
  ({ title, shortDescription, description, url, items, renderItem, content, unlockPaywalledContent = false }) => {
    // const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    const { session } = useAuthenticated()
    const canViewPaywalled = !!(session?.user?.isPremium || unlockPaywalledContent)
    // const [showFundingMore, setShowFundingMore] = useState(true)
    const [showFundingMore, setShowFundingMore] = useState(true)
    const [showViewMoreButton, setShowViewMoreButton] = useState(false)
    const fundingTextRef = useRef<HTMLParagraphElement>(null)

    // Find the funding details item index
    const fundingDetailsIndex = items.findIndex(
      (item) => item.title?.toLocaleLowerCase() === FUNDING_DETAILS.toLocaleLowerCase(),
    )

    useEffect(() => {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      const checkHeight = () => {
        if (fundingTextRef.current) {
          const element = fundingTextRef.current
          // Calculate 2 lines height: 2 * line-height (18px) = 36px
          const twoLinesHeight = 36

          // Check if the actual scroll height exceeds 2 lines
          if (element.scrollHeight > twoLinesHeight) {
            setShowViewMoreButton(true)
          } else {
            setShowViewMoreButton(false)
          }
        }
      }

      // Wait for next frame to ensure DOM is rendered
      const rafId = requestAnimationFrame(() => {
        // Double RAF to ensure layout is complete
        requestAnimationFrame(() => {
          checkHeight()
        })
      })

      return () => {
        cancelAnimationFrame(rafId)
      }
    }, [items, canViewPaywalled, fundingDetailsIndex])

    const isDiverseLeadership = (item: SectionItem) => {
      if (item.isDiverseLeadership) {
        return (
          <ThemeOnly theme="legaltech">
            <div className="!tracking-normal !normal-case inline-flex w-auto bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] px-[20px] pt-[6px] pb-[7px]">
              Diverse Leadership
            </div>
          </ThemeOnly>
        )
      }
    }

    const [showmoreClassName, setShowmoreClassName] = useState('h-[36px] overflow-hidden')

    return (
      <div key={title + 'SectionComp'} className="w-full border-[1px] border-mist rounded-[5px] mt-[2rem]">
        <CartHeader>
          <CartHeaderTitle>{title}</CartHeaderTitle>
        </CartHeader>

        <div className={`flex flex-col mt-[2.0rem] `}>
          {shortDescription && <ShortDescItem shortDescription={shortDescription} />}

          {checkNotEmpty(description) && (
            <div className="value-proposition">
              <LongDescItem longDescription={description} />{' '}
            </div>
          )}

          <div className={`flex flex-wrap w-full px-[2.0rem] relative company-card-container`}>
            {checkNotEmpty(url) && (
              <div className={'flex flex-col w-1/2'}>
                <div className="flex flex-col mb-[2.0rem]">
                  <CartSubTitle>RESOURCES</CartSubTitle>
                  <IconLink url={url} title="Official website" iconColor="#011D58" />
                </div>
              </div>
            )}
            {items.map((item: SectionItem, index: number) => (
              <Fragment key={index}>
                {item.isNewLine && <div className="w-full h-[0px]"></div>}
                {item.type === 'LinkedinIconLink' && (
                  <div className="absolute top-0 right-7 flex flex-col mb-[2.0rem]">
                    <IconLinkLinkedin url={item.items[0]} title={item.title} />
                  </div>
                )}
                {item.type === 'TwitterIconLink' && (
                  <div className="absolute top-10 right-7 flex flex-col mb-[2.0rem]">
                    <IconLinkTwitter url={item.items[0]} title={item?.title || ''} />
                  </div>
                )}
                {!['LinkedinIconLink', 'TwitterIconLink'].includes(item.type) && (
                  <div
                    className={cn(
                      'flex w-full',
                      item.isFullWidth ? '' : 'md:w-1/2',
                      item.isHorizontal ? 'flex-row' : 'flex-col',
                      item.className ? item.className : '',
                    )}
                  >
                    {item.type === 'SpanDataList' && item.subType && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <SpanDataList
                          isProductTypes={item.title === 'PRODUCT TYPES'}
                          isPracticeAreas={item.title === 'PRACTICE AREAS'}
                          features={item.items}
                          type={item.subType}
                        />
                      </div>
                    )}
                    {item.type === 'SpanDataIcon' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <SpanDataIcon features={item.items} />
                      </div>
                    )}
                    {item.type === 'DottedDataList' && item.items.length > 0 && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <DottedDataList
                            key={'DottedDataList-' + index}
                            isSecurity={
                              item.title === 'Security' ||
                              item.title === 'Total Funding Amount' ||
                              item.title === 'Latest Funding Round' ||
                              item.title === 'Value Proposition'
                            }
                            isDataHostingLocations={item.title === 'Data Hosting Locations'}
                            isServiceOfferings={item.title === 'SERVICE OFFERINGS'}
                            isValueProposition={item.title === 'Value Proposition'}
                            features={item.items || []}
                            colValue={item.colValue}
                            title={item.title}
                            parentGridCols={item?.parentGridCols || 0}
                            childGridCols={item?.childGridCols || 0}
                            unlockPaywalledContent={unlockPaywalledContent}
                          />
                        </div>
                      </div>
                    )}
                    {item.type === 'string' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <div
                            className={cn(
                              'flex flex-wrap items-center',
                              item.title?.toLocaleLowerCase() === FUNDING_DETAILS.toLocaleLowerCase() &&
                                !canViewPaywalled
                                ? 'mt-[13px]'
                                : '',
                            )}
                          >
                            <CartSubContent type="list" className="mr-[0.125rem] whitespace-pre-wrap list-none">
                              {item.title === 'Value Proposition' ? (
                                <div className="value-proposition">
                                  <div dangerouslySetInnerHTML={{ __html: item.sValue || '' }} />
                                </div>
                              ) : item.title?.toLocaleLowerCase() === FUNDING_DETAILS.toLocaleLowerCase() &&
                                canViewPaywalled ? (
                                <div className="flex flex-row">
                                  <LockOpenComponent />
                                  <div>
                                    <div
                                      id={`fundingDetails-${index}`}
                                      key={`fundingDetails-${index}`}
                                      className="mt-[13px] w-full text-[0.875rem] font-primary font-regular leading-[19px]"
                                    >
                                      <p
                                        ref={index === fundingDetailsIndex ? fundingTextRef : null}
                                        id={`fundingDetails-text-${index}`}
                                        key={`fundingDetails-text-${index}`}
                                        className={cn(showmoreClassName, '!text-[15px] leading-[18px] !mb-0', {})}
                                      >
                                        {item.sValue}
                                      </p>
                                    </div>
                                    <div
                                      className={cn(
                                        'w-full text-[0.875rem] font-primary font-regular text-textdark leading-[11px] cursor-pointer',
                                        {
                                          hidden: !showViewMoreButton,
                                        },
                                      )}
                                      onClick={() => {
                                        setShowFundingMore(!showFundingMore)
                                        setShowmoreClassName(showFundingMore ? '' : 'h-[36px] overflow-hidden')
                                      }}
                                    >
                                      {showFundingMore ? 'View more' : 'View less'}
                                    </div>
                                  </div>
                                </div>
                              ) : item.title?.toLocaleLowerCase() === FUNDING_DETAILS.toLocaleLowerCase() &&
                                !canViewPaywalled ? (
                                <LikeHiddenComponent title="Premium User Only" fromPage="compare" />
                              ) : (
                                <div dangerouslySetInnerHTML={{ __html: item.sValue || '' }} />
                              )}
                            </CartSubContent>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.type === 'number' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <div className="flex flex-col">
                          <CartSubTitle>{item.title}</CartSubTitle>
                          <div className="flex flex-wrap items-center">
                            <CartSubContent type="list" className="mr-[0.125rem] list-none">
                              {item.nValue || ''}
                            </CartSubContent>
                          </div>
                        </div>
                      </div>
                    )}
                    {item.type === 'dedicated' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        <CartSubTitle>{item.title}</CartSubTitle>
                        <div>
                          {item.dValue &&
                            item.dValue.length > 0 &&
                            item.dValue.map((rc: DedicatedResourcesType, index: number) => {
                              const formattedType = rc.type.replace(/([a-z])([A-Z])/g, '$1 $2')
                              return (
                                <CartSubContent type="list" key={index} className="mr-[0.125rem]">
                                  {`${formattedType} : ${rc.count}`}
                                  {/* {index !== (item.dValue ? item.dValue.length : 0) - 1 ? ',' : ''} */}
                                </CartSubContent>
                              )
                            })}
                        </div>
                      </div>
                    )}
                    {item.type === 'CustomList' &&
                      renderItem &&
                      item.items.some(
                        (subItem) =>
                          (subItem.title && subItem.title.trim() !== '') ||
                          (subItem.externalLink && subItem.externalLink.trim() !== '') ||
                          (subItem.name && subItem.name.trim() !== ''),
                      ) && (
                        <div className="flex flex-col mb-[2.0rem]">
                          <CartSubTitle>
                            <div className="flex flex-row items-center md:justify-start justify-between md:gap-x-0 gap-x-[10px]   gap-y-1">
                              <span className="md:w-1/2 h-full">{item.title}</span>
                              {item.isDiverseLeadership && isDiverseLeadership(item)}
                            </div>
                          </CartSubTitle>
                          <div className={cn(`mt-[13px]`)}>
                            <ul className="custom-bullet flex flex-col pl-5 space-y-2 list-none">
                              {item.items
                                .filter((subItem) =>
                                  [subItem?.title, subItem?.name].some((field) => field && field.trim() !== ''),
                                )
                                .map((subItem, idx) => (
                                  <li key={idx} className="pl-4 flex items-center space-x-3">
                                    {renderItem(subItem)}
                                  </li>
                                ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    {item.type === 'CheckDataList' && (
                      <div className="flex flex-col mb-[2.0rem]">
                        {item?.title == 'OPEN_TO_INVESTMENT' ? (
                          <>
                            <div className="flex flex-row">
                              <b>
                                <CheckDataList features={item.items || []} isBold={true} forField={item.title} />
                              </b>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex flex-col">
                              <CartSubTitle className="mb-[14px]">{item.title}</CartSubTitle>
                              <CheckDataList features={item.items || []} />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Fragment>
            ))}
            {content && (
              <div className="flex flex-col mb-[2.0rem]">
                <HtmlContentViewer html={content} />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
)
